// qs-brushes-index.ts
import type { BrushType } from '@/types/qs-types-index'
import type {
  BrushConfig,
  BrushContext,
  BrushDrawingResult,
  BrushInstance,
  BrushProperties,
  BrushRecreationContext
} from '@/brushes/qs-brushes-types'
import type { BaseBrush } from '@/brushes/base/qs-brushes-base'

import { brushRegistry } from '@/brushes/qs-brushes-registry'
import { BowTieBrush } from '@/brushes/brushes/qs-brush-BowTie'
import { DashedBrush } from '@/brushes/brushes/qs-brush-Dashed'
import { PointedBrush } from '@/brushes/brushes/qs-brush-Pointed'
import { RoundBrush } from '@/brushes/brushes/qs-brush-Round'
import { SprayBrush } from '@/brushes/brushes/qs-brush-Spray'
import { SquareBrush } from '@/brushes/brushes/qs-brush-Square'
import { ThickToThinBrush } from '@/brushes/brushes/qs-brush-ThickToThin'
import { ThinToThickBrush } from '@/brushes/brushes/qs-brush-ThinToThick'

export type {
  BrushConfig,
  BrushContext,
  BrushDrawingResult,
  BrushInstance,
  BrushProperties,
  BrushRecreationContext
}

export { brushRegistry }

brushRegistry.register(new RoundBrush())
brushRegistry.register(new SquareBrush())
brushRegistry.register(new DashedBrush())
brushRegistry.register(new SprayBrush())
brushRegistry.register(new PointedBrush())
brushRegistry.register(new BowTieBrush())
brushRegistry.register(new ThickToThinBrush())
brushRegistry.register(new ThinToThickBrush())

brushRegistry.setBrushTypeMapping('round', 'round')
brushRegistry.setBrushTypeMapping('square', 'square')
brushRegistry.setBrushTypeMapping('dashed', 'dashed')
brushRegistry.setBrushTypeMapping('spray', 'spray')
brushRegistry.setBrushTypeMapping('pointed', 'pointed')
brushRegistry.setBrushTypeMapping('bow-tie', 'bow-tie')
brushRegistry.setBrushTypeMapping('thick-to-thin', 'thick-to-thin')
brushRegistry.setBrushTypeMapping('thin-to-thick', 'thin-to-thick')

export class BrushFactory {
  static createBrush(brushId: string, size: number, color: string, opacity: number): BrushInstance {
    const brush = brushRegistry.getBrush(brushId)
    if (!brush) {
      throw new Error(`Brush not found: ${brushId}`)
    }

    return {
      id: brushId,
      config: brush.config,
      size: brush.getValidSize(size),
      color,
      opacity
    }
  }

  static createBrushFromType(brushType: BrushType, size: number, color: string, opacity: number): BrushInstance {
    const brush = brushRegistry.getBrushByType(brushType)
    if (!brush) {
      throw new Error(`Brush type not found: ${brushType}`)
    }

    return this.createBrush(brush.config.id, size, color, opacity)
  }
}

export const getBrush = (id: string): BaseBrush | undefined => brushRegistry.getBrush(id)
export const getAllBrushes = (): readonly BaseBrush[] => brushRegistry.getAllBrushes()
export const getBrushesByCategory = (category: string): readonly BaseBrush[] => brushRegistry.getBrushesByCategory(category)
export const hasBrush = (id: string): boolean => brushRegistry.hasBrush(id)